// 用户设置管理工具
import { apiFetch } from './api';

// 设置键名常量
export const SETTING_KEYS = {
  // 时间范围设置
  START_TIME: 'startTime',
  END_TIME: 'endTime',
  
  // 筛选设置
  HIDE_INVALID: 'hideInvalid',
  SHOW_ONLINE_ONLY: 'showOnlineOnly',
  
  // 轨迹模式设置
  TRACK_MODE: 'trackMode',
  TRACK_MINUTES: 'trackMinutes',
  TRACK_HOURS: 'trackHours',
  TRACK_DAYS: 'trackDays',
  
  // 轨迹回放设置
  PLAYBACK_START: 'playbackStart',
  PLAYBACK_END: 'playbackEnd',
  
  // 界面设置
  SIDEBAR_WIDTH: 'sidebarWidth',
  COLLAPSED: 'collapsed',
  MAP_STYLE: 'mapStyle',
  
  // 轨迹颜色设置
  TRACK_COLORS: 'trackColors',
  
  // 显示设置
  SHOW_DOWNLOAD: 'showDownload',
  SHOW_TRACKS: 'showTracks',
  ALL_TRACKS_VISIBLE: 'allTracksVisible',
  SHOW_MEMBER_NAMES: 'showMemberNames',

  // 刷新间隔
  REFRESH_INTERVAL: 'refreshInterval',

  // 任务中（看板成员/临时分组，刷新不丢）
  MISSION_MEMBER_IDS: 'missionMemberIds',
  MISSION_GROUPS: 'missionGroups',
  MISSION_MODE: 'missionMode',
  MISSIONS: 'missions',
  ACTIVE_MISSION_ID: 'activeMissionId',
  MEMBER_PANEL_TAB: 'memberPanelTab'
};

// 默认设置值
export const DEFAULT_SETTINGS = {
  [SETTING_KEYS.START_TIME]: '',
  [SETTING_KEYS.END_TIME]: '',
  [SETTING_KEYS.HIDE_INVALID]: false,
  [SETTING_KEYS.SHOW_ONLINE_ONLY]: false,
  [SETTING_KEYS.TRACK_MODE]: 'recent',
  [SETTING_KEYS.TRACK_MINUTES]: 60,
  [SETTING_KEYS.TRACK_HOURS]: 1,
  [SETTING_KEYS.TRACK_DAYS]: 1,
  [SETTING_KEYS.PLAYBACK_START]: null,
  [SETTING_KEYS.PLAYBACK_END]: null,
  [SETTING_KEYS.SIDEBAR_WIDTH]: 480,
  [SETTING_KEYS.COLLAPSED]: false,
  [SETTING_KEYS.MAP_STYLE]: 'mapbox://styles/mapbox/satellite-streets-v12',
  [SETTING_KEYS.TRACK_COLORS]: {},
  [SETTING_KEYS.SHOW_DOWNLOAD]: true,
  [SETTING_KEYS.SHOW_TRACKS]: {},
  [SETTING_KEYS.ALL_TRACKS_VISIBLE]: false,
  [SETTING_KEYS.SHOW_MEMBER_NAMES]: true,
  [SETTING_KEYS.REFRESH_INTERVAL]: 60000,
  [SETTING_KEYS.MISSION_MEMBER_IDS]: [],
  [SETTING_KEYS.MISSION_GROUPS]: [],
  [SETTING_KEYS.MISSION_MODE]: false,
  [SETTING_KEYS.MISSIONS]: [],
  [SETTING_KEYS.ACTIVE_MISSION_ID]: null,
  [SETTING_KEYS.MEMBER_PANEL_TAB]: 'all'
};

// 合并设置到 localStorage，避免部分保存覆盖已有配置
const mergeAndPersistLocalSettings = (partialSettings, userId) => {
  const localSettingsKeys = [
    `user_settings_${userId || 'guest'}`,
    'userSettings',
    'user_settings'
  ];

  let baseSettings = { ...DEFAULT_SETTINGS };
  for (const storageKey of localSettingsKeys) {
    const existing = localStorage.getItem(storageKey);
    if (existing) {
      try {
        baseSettings = { ...DEFAULT_SETTINGS, ...JSON.parse(existing) };
        break;
      } catch (e) {
        console.error(`解析localStorage设置失败 (${storageKey}):`, e);
      }
    }
  }

  const mergedSettings = { ...baseSettings, ...partialSettings };

  for (const storageKey of localSettingsKeys) {
    localStorage.setItem(storageKey, JSON.stringify(mergedSettings));
  }

  return mergedSettings;
};

// 获取当前用户ID（从localStorage或token中解析）
export const getCurrentUserId = () => {
  // 首先尝试从localStorage获取用户ID
  const userInfo = localStorage.getItem('userInfo');
  if (userInfo) {
    try {
      const user = JSON.parse(userInfo);
      if (user.id) return parseInt(user.id);
    } catch (e) {
      console.error('解析用户信息失败:', e);
    }
  }
  
  // 尝试从JWT token中解析用户ID
  const token = localStorage.getItem('token');
  if (token) {
    try {
      // JWT token的格式是: header.payload.signature
      const payload = token.split('.')[1];
      if (payload) {
        const decoded = JSON.parse(atob(payload));
        if (decoded.id) {
          console.log('从JWT token解析到用户ID:', decoded.id);
          return parseInt(decoded.id);
        }
      }
    } catch (e) {
      console.error('解析JWT token失败:', e);
    }
  }
  
  // 如果没有用户ID，返回null表示未登录
  return null;
};

// 加载用户设置
export const loadUserSettings = async () => {
  try {
    const userId = getCurrentUserId();
    
    // 如果用户已登录，优先从后端加载
    if (userId) {
      try {
        const response = await apiFetch(`/api/user-settings/${userId}`);
        const data = await response.json();
        
        if (data.success && data.settings) {
          // 合并默认设置和用户设置
          const settings = { ...DEFAULT_SETTINGS, ...data.settings };
          // 同时保存到localStorage作为备用
          localStorage.setItem(`user_settings_${userId}`, JSON.stringify(settings));
          console.log('从后端加载用户设置:', settings);
          return settings;
        } else {
          console.log('后端没有用户设置数据，尝试从localStorage加载');
        }
      } catch (e) {
        console.error('从后端加载设置失败:', e);
        console.log('后端加载失败，尝试从localStorage加载');
      }
    }
    
    // 从localStorage加载（支持多种键名格式）
    const localSettingsKeys = [
      `user_settings_${userId || 'guest'}`,
      'userSettings',
      'user_settings'
    ];
    
    for (const key of localSettingsKeys) {
      const localSettings = localStorage.getItem(key);
      if (localSettings) {
        try {
          const parsed = JSON.parse(localSettings);
          console.log(`从localStorage加载设置 (${key}):`, parsed);
          // 优先使用用户保存的设置，只有在用户设置中没有的字段才使用默认值
          return { ...DEFAULT_SETTINGS, ...parsed };
        } catch (e) {
          console.error(`解析localStorage设置失败 (${key}):`, e);
        }
      }
    }
    
    // 如果所有方法都失败，返回默认设置
    console.log('使用默认设置');
    return DEFAULT_SETTINGS;
  } catch (e) {
    console.error('加载用户设置异常:', e);
    return DEFAULT_SETTINGS;
  }
};

// 保存用户设置
export const saveUserSettings = async (settings) => {
  try {
    const userId = getCurrentUserId();
    const mergedSettings = mergeAndPersistLocalSettings(settings, userId);
    console.log('设置已保存到localStorage:', mergedSettings);
    
    // 如果用户已登录，尝试保存到后端
    if (userId) {
      try {
        const response = await apiFetch(`/api/user-settings/${userId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ settings: mergedSettings })
        });
        
        const data = await response.json();
        if (data.success) {
          console.log('设置已保存到后端');
          return true;
        } else {
          console.error('保存到后端失败:', data.error);
        }
      } catch (e) {
        console.error('保存到后端失败:', e);
      }
    } else {
      console.log('用户未登录，仅保存到localStorage');
    }
    
    // 即使后端保存失败，localStorage已经保存了，所以返回true
    return true;
  } catch (e) {
    console.error('保存用户设置异常:', e);
    return false;
  }
};

// 保存单个设置项
export const saveSetting = async (key, value) => {
  try {
    const userId = getCurrentUserId();
    const mergedSettings = mergeAndPersistLocalSettings({ [key]: value }, userId);
    
    // 如果用户已登录，尝试保存到后端
    if (userId) {
      try {
        const response = await apiFetch(`/api/user-settings/${userId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            settings: mergedSettings
          })
        });
        
        const data = await response.json();
        return data.success;
      } catch (e) {
        console.error('保存设置项到后端失败:', e);
        return false;
      }
    } else {
      console.log('用户未登录，仅保存到localStorage');
      return true;
    }
  } catch (e) {
    console.error('保存设置项异常:', e);
    return false;
  }
};

// 删除设置项
export const deleteSetting = async (key) => {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      console.warn('未找到用户ID，无法删除设置');
      return false;
    }

    const response = await apiFetch(`/api/user-settings/${userId}/${key}`, {
      method: 'DELETE'
    });
    
    const data = await response.json();
    return data.success;
  } catch (e) {
    console.error('删除设置项异常:', e);
    return false;
  }
};

// 防抖保存函数
let saveTimeout = null;
export const debouncedSave = (settings, delay = 1000) => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }
  
  saveTimeout = setTimeout(() => {
    saveUserSettings(settings);
  }, delay);
}; 